import type { Locale } from "@/lib/i18n";
import type { ChallengeId, ScopeIntentId } from "@/lib/quote-links";

export type ResourceCluster =
  | "performance"
  | "conversion"
  | "migration"
  | "technical-seo"
  | "development"
  | "general";

export type ResourceContentType =
  | "pillar"
  | "tactical"
  | "buyer_intent"
  | "field_note"
  | "case_note";

export type CommercialOfferId =
  | "performance_fix"
  | "cro_technical_seo_fix"
  | "custom_build_replatform";

export type PrimaryServiceId =
  | "performance"
  | "cro"
  | "technical_seo"
  | "migration"
  | "development"
  | "analytics";

export type MigrationMode = "standard" | "plus" | null;

export interface ResourceTaxonomyInput {
  slug?: string | null;
  slug_es?: string | null;
  title_en?: string | null;
  title_es?: string | null;
  tags_en?: string[] | null;
  tags_es?: string[] | null;
  cluster?: ResourceCluster | null;
  content_type?: ResourceContentType | null;
  commercial_offer?: CommercialOfferId | null;
  primary_service?: PrimaryServiceId | null;
  pillar_slug?: string | null;
}

export interface ResourceCommercialContext {
  cluster: ResourceCluster;
  contentType: ResourceContentType;
  offer: CommercialOfferId;
  service: PrimaryServiceId;
  migrationMode: MigrationMode;
  intent: ScopeIntentId;
  challenge: ChallengeId;
  source: string;
  pillarHref: string;
  pillarLabel: string;
  serviceHref: string;
  serviceLabel: string;
  offerLabel: string;
  offerSummary: string;
  scopeLabel: string;
  secondaryCtaLabel: string;
}

const COPY = {
  en: {
    offers: {
      performance_fix: {
        label: "Performance Fix",
        summary: "A bounded project for speed, Core Web Vitals, mobile interaction, and app contention.",
      },
      cro_technical_seo_fix: {
        label: "CRO & Technical SEO Fix",
        summary: "A bounded project for conversion friction, crawling, indexation, architecture, and buying experience.",
      },
      custom_build_replatform: {
        label: "Custom Build or Replatform",
        summary: "A bounded project for themes, migrations, apps, integrations, B2B, and architecture changes.",
      },
    },
    pillars: {
      performance: "Shopify performance pillar guide",
      conversion: "Shopify CRO pillar guide",
      migration: "Shopify migration resource hub",
      "technical-seo": "Shopify technical SEO resource hub",
      development: "Custom build and replatform guide",
      general: "Shopify growth resources",
    },
    services: {
      performance: "Shopify Performance",
      cro: "Shopify CRO",
      technical_seo: "Shopify Technical SEO",
      migration: "Shopify Migration Services",
      development: "Shopify Development",
      analytics: "Shopify Analytics & AI",
    },
    scope: "Request a scope",
  },
  es: {
    offers: {
      performance_fix: {
        label: "Performance Fix",
        summary: "Un proyecto acotado para velocidad, Core Web Vitals, interacción móvil y conflictos entre apps.",
      },
      cro_technical_seo_fix: {
        label: "CRO & Technical SEO Fix",
        summary: "Un proyecto acotado para fricción de conversión, crawling, indexación, arquitectura y experiencia de compra.",
      },
      custom_build_replatform: {
        label: "Custom Build or Replatform",
        summary: "Un proyecto acotado para themes, migraciones, apps, integraciones, B2B y cambios de arquitectura.",
      },
    },
    pillars: {
      performance: "Guía pilar de performance Shopify",
      conversion: "Guía pilar de CRO para Shopify",
      migration: "Hub de recursos de migración a Shopify",
      "technical-seo": "Hub de recursos de SEO técnico Shopify",
      development: "Guía de desarrollo y replatforming",
      general: "Recursos de growth para Shopify",
    },
    services: {
      performance: "Performance Shopify",
      cro: "CRO para Shopify",
      technical_seo: "SEO técnico para Shopify",
      migration: "Servicios de migración a Shopify",
      development: "Desarrollo Shopify",
      analytics: "Analítica e IA para Shopify",
    },
    scope: "Solicitar un scope",
  },
} as const;

const MIGRATION_COPY = {
  en: {
    standard: {
      offerLabel: "Shopify Migration & Replatforming",
      offerSummary: "An end-to-end migration project covering platform selection, catalog and customer data, redirects, integrations, storefront implementation, QA, launch, and stabilization. The destination can be standard Shopify or Shopify Plus according to the actual requirements.",
      serviceLabel: "Shopify Migration Services",
      scopeLabel: "Scope my Shopify migration",
      secondaryCtaLabel: "Explore migration services",
    },
    plus: {
      offerLabel: "Shopify Plus Migration",
      offerSummary: "A premium migration program for B2B, checkout extensibility, Functions, complex Markets or multi-store structures, enterprise integrations, and higher release-governance requirements.",
      serviceLabel: "Shopify Plus Engineering",
      scopeLabel: "Scope my Shopify Plus migration",
      secondaryCtaLabel: "Talk to a Shopify Plus specialist",
    },
  },
  es: {
    standard: {
      offerLabel: "Migración a Shopify",
      offerSummary: "Un proyecto end-to-end que cubre selección de plataforma, catálogo, clientes, redirects, integraciones, storefront, QA, lanzamiento y estabilización. El destino puede ser Shopify estándar o Shopify Plus según los requisitos reales.",
      serviceLabel: "Servicios de migración a Shopify",
      scopeLabel: "Cotizar mi migración a Shopify",
      secondaryCtaLabel: "Ver servicios de migración",
    },
    plus: {
      offerLabel: "Migración a Shopify Plus",
      offerSummary: "Un programa premium para B2B, checkout extensibility, Functions, estructuras complejas de Markets o múltiples tiendas, integraciones enterprise y mayor gobierno de releases.",
      serviceLabel: "Ingeniería Shopify Plus",
      scopeLabel: "Cotizar mi migración a Shopify Plus",
      secondaryCtaLabel: "Hablar con un especialista Shopify Plus",
    },
  },
} as const;

const SERVICE_PATHS: Record<PrimaryServiceId, Record<Locale, string>> = {
  performance: { en: "/shopify-performance", es: "/es/rendimiento" },
  cro: { en: "/shopify-cro", es: "/es/cro-shopify" },
  technical_seo: { en: "/shopify-technical-seo", es: "/es/seo-tecnico-shopify" },
  migration: { en: "/shopify-migration", es: "/es/migracion-shopify" },
  development: { en: "/shopify-experts", es: "/es/desarrollo-shopify" },
  analytics: { en: "/analytics-ai", es: "/es/analitica-ai" },
};

const PLUS_SERVICE_PATHS: Record<Locale, string> = {
  en: "/shopify-plus-agency",
  es: "/es/agencia-shopify-plus",
};

const PILLAR_PATHS: Record<ResourceCluster, Record<Locale, string>> = {
  performance: {
    en: "/resources/shopify-performance-optimization-guide",
    es: "/es/recursos/guia-optimizacion-rendimiento-shopify",
  },
  conversion: {
    en: "/resources/shopify-conversion-rate-optimization-playbook",
    es: "/es/recursos/playbook-cro-shopify",
  },
  migration: {
    en: "/resources/migration",
    es: "/es/recursos/migracion",
  },
  "technical-seo": {
    en: "/resources/technical-seo",
    es: "/es/recursos/seo-tecnico",
  },
  development: {
    en: "/resources/migration",
    es: "/es/recursos/migracion",
  },
  general: { en: "/resources", es: "/es/recursos" },
};

const PLUS_MIGRATION_PILLARS: Record<Locale, string> = {
  en: "/resources/shopify-plus-migration",
  es: "/es/recursos/playbook-replatforming-shopify",
};

function normalizedHaystack(input: ResourceTaxonomyInput, locale: Locale, pathname: string): string {
  const tags = locale === "es" ? (input.tags_es?.length ? input.tags_es : input.tags_en) : input.tags_en;
  return [pathname, input.slug, input.slug_es, input.title_en, input.title_es, ...(tags ?? [])]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function inferCluster(haystack: string): ResourceCluster {
  if (/performance|speed|core web vitals|\blcp\b|\binp\b|\bcls\b|rendimiento|velocidad/.test(haystack)) return "performance";
  if (/migration|migraci[oó]n|replatform|bigcommerce|magento|woocommerce|shopify plus upgrade/.test(haystack)) return "migration";
  if (/technical seo|seo t[eé]cnico|indexation|indexaci[oó]n|crawl|canonical|schema|structured data|hreflang|rendering/.test(haystack)) return "technical-seo";
  if (/development|desarrollo|liquid|hydrogen|headless|shopify functions|custom app|integraci[oó]n|erp|b2b/.test(haystack)) return "development";
  if (/cro|conversion|conversi[oó]n|checkout|pdp|cart|carrito|analytics|anal[ií]tica|tracking/.test(haystack)) return "conversion";
  return "general";
}

function inferMigrationMode(cluster: ResourceCluster, haystack: string): MigrationMode {
  if (cluster !== "migration") return null;
  const plusSignals = /shopify\s*plus|plus\s+migration|migraci[oó]n\s+(?:a\s+)?shopify\s*plus|upgrade\s+to\s+shopify\s*plus|\bb2b\b|checkout extensibility|shopify functions|complex markets|multi[- ]store|enterprise migration/;
  return plusSignals.test(haystack) ? "plus" : "standard";
}

function inferService(cluster: ResourceCluster, haystack: string): PrimaryServiceId {
  if (cluster === "technical-seo" || /technical seo|seo t[eé]cnico|indexation|indexaci[oó]n|crawl|canonical|faceted/.test(haystack)) return "technical_seo";
  if (/analytics|anal[ií]tica|tracking|ga4|attribution|atribuci[oó]n|ai commerce|agentic/.test(haystack)) return "analytics";
  if (cluster === "performance") return "performance";
  if (cluster === "migration") return "migration";
  if (cluster === "development") return "development";
  return "cro";
}

function inferOffer(cluster: ResourceCluster): CommercialOfferId {
  if (cluster === "performance") return "performance_fix";
  if (cluster === "migration" || cluster === "development") return "custom_build_replatform";
  return "cro_technical_seo_fix";
}

function offerRouting(offer: CommercialOfferId): Pick<ResourceCommercialContext, "intent" | "challenge"> {
  if (offer === "performance_fix") return { intent: "improve", challenge: "slow" };
  if (offer === "custom_build_replatform") return { intent: "rebuild", challenge: "migrating" };
  return { intent: "improve", challenge: "not_converting" };
}

function normalizePillarHref(pillarSlug: string | null | undefined, locale: Locale): string | null {
  const value = pillarSlug?.trim();
  if (!value) return null;
  if (value.startsWith("/")) return value;
  return locale === "es" ? `/es/recursos/${value}` : `/resources/${value}`;
}

export function resolveResourceCommercialContext({ resource = {}, locale, pathname }: { resource?: ResourceTaxonomyInput; locale: Locale; pathname: string }): ResourceCommercialContext {
  const haystack = normalizedHaystack(resource, locale, pathname);
  const cluster = resource.cluster ?? inferCluster(haystack);
  const offer = resource.commercial_offer ?? inferOffer(cluster);
  const service = cluster === "migration" ? "migration" : resource.primary_service ?? inferService(cluster, haystack);
  const contentType = resource.content_type ?? (pathname.includes("field-note") ? "field_note" : "tactical");
  const migrationMode = inferMigrationMode(cluster, haystack);
  const routing = offerRouting(offer);
  const copy = COPY[locale];
  const migrationCopy = migrationMode ? MIGRATION_COPY[locale][migrationMode] : null;
  const explicitPillar = normalizePillarHref(resource.pillar_slug, locale);
  const pillarHref = explicitPillar ?? (migrationMode === "plus" ? PLUS_MIGRATION_PILLARS[locale] : PILLAR_PATHS[cluster][locale]);
  const serviceHref = migrationMode === "plus" ? PLUS_SERVICE_PATHS[locale] : SERVICE_PATHS[service][locale];

  return {
    cluster,
    contentType,
    offer,
    service,
    migrationMode,
    ...routing,
    source: pathname,
    pillarHref,
    pillarLabel: copy.pillars[cluster],
    serviceHref,
    serviceLabel: migrationCopy?.serviceLabel ?? copy.services[service],
    offerLabel: migrationCopy?.offerLabel ?? copy.offers[offer].label,
    offerSummary: migrationCopy?.offerSummary ?? copy.offers[offer].summary,
    scopeLabel: migrationCopy?.scopeLabel ?? copy.scope,
    secondaryCtaLabel: migrationCopy?.secondaryCtaLabel ?? copy.services[service],
  };
}

export function resourceMatchesCluster(resource: ResourceTaxonomyInput, cluster: Exclude<ResourceCluster, "general">, locale: Locale): boolean {
  if (resource.cluster) return resource.cluster === cluster;
  return inferCluster(normalizedHaystack(resource, locale, resource.slug ?? "")) === cluster;
}
